import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Typography,
  Button,
  Tag,
  Input,
} from "antd";
import { MenuOutlined } from "@ant-design/icons";
import axios from "axios";
import Sidebar from "../../Components/Sidebar";
import { BASE_URL } from "../../Config";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const CATEGORIES = {
  "yoga-content": "Yoga content",
  "yoga-name":    "Yoga name",
  "yoga-image":   "Yoga image",
  "dictionary":   "Dictionary",
  "other":        "Other",
};

const ErrorReportApproval = () => {
  const [collapsed, setCollapsed]   = useState(false);
  const [isMobile, setIsMobile]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reports, setReports]       = useState([]);
  const [notes, setNotes]           = useState({});
  const [loading, setLoading]       = useState({});

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => { fetchPending(); }, []);

  const getToken = () => sessionStorage.getItem("token");

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/error-report/admin/pending`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.data.success) setReports(res.data.data);
    } catch {}
  };

  const handleApprove = async (id) => {
    setLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.put(
        `${BASE_URL}/error-report/admin/approve/${id}`,
        { adminNote: notes[id] || "" },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setReports((p) => p.filter((r) => r._id !== id));
    } catch (err) {
      alert(err.response?.data?.error || "Approval failed.");
    } finally {
      setLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const handleReject = async (id) => {
    if (!notes[id]?.trim()) {
      alert("Please enter a rejection reason before rejecting.");
      return;
    }
    setLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.put(
        `${BASE_URL}/error-report/admin/reject/${id}`,
        { adminNote: notes[id] },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setReports((p) => p.filter((r) => r._id !== id));
    } catch (err) {
      alert(err.response?.data?.error || "Rejection failed.");
    } finally {
      setLoading((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        collapsedWidth="0"
        role="admin"
      />

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 0 : 240,
          transition: "margin 0.3s",
        }}
      >
        {/* Header */}
        <div className="MainHeader" style={{
          display: "flex", alignItems: "center",
          padding: isMobile ? "0 16px" : "0 24px",
        }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileOpen(true)}
              style={{ fontSize: 18, marginRight: 12 }}
            />
          )}
          <Title level={4} style={{ margin: 0, flex: 1 }}>
            Error Report Approval
          </Title>
          <Tag color="orange" style={{ fontSize: 13, padding: "2px 12px" }}>
            {reports.length} Pending
          </Tag>
        </div>

        <Content style={{ padding: isMobile ? 12 : 24 }}>

          {/* Info banner */}
          <Card
            style={{
              borderRadius: 12, marginBottom: 20,
              background: "#fffbeb", border: "1px solid #fde68a",
            }}
            bodyStyle={{ padding: "14px 20px" }}
          >
            <Text style={{ fontSize: 13 }}>
              ✦ Approving a report adds <strong>+2 days</strong> to the user's
              media link extension. Maximum <strong>10 valid reports</strong> per
              user per month.
            </Text>
          </Card>

          {/* Empty state */}
          {reports.length === 0 ? (
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 60 }}>
              <div style={{ textAlign: "center", color: "#9ca3af" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                <Text type="secondary">No pending error reports.</Text>
              </div>
            </Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {reports.map((r) => (
                <Card
                  key={r._id}
                  style={{ borderRadius: 12, border: "1px solid #e5e7eb" }}
                  bodyStyle={{ padding: 20 }}
                >
                  {/* Top row */}
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexWrap: "wrap",
                    gap: 8, marginBottom: 12,
                  }}>
                    <Tag
                      color="purple"
                      style={{ borderRadius: 20, padding: "2px 12px", fontSize: 12 }}
                    >
                      {CATEGORIES[r.category] || r.category}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </Text>
                  </div>

                  {/* Description */}
                  <div style={{
                    background: "#f9fafb", borderRadius: 8,
                    padding: "10px 14px", marginBottom: 12,
                  }}>
                    <Text style={{ fontSize: 14 }}>{r.description}</Text>
                  </div>

                  {/* User ID */}
                 {/* User ID */}
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                    User ID: {r.userId}
                  </Text>

                  {/* Proof image */}
                  {r.proofImageUrl && (
                    <div style={{ marginBottom: 12 }}>
                      <Text style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                        📎 Proof Screenshot:
                      </Text>
                      <a href={r.proofImageUrl} target="_blank" rel="noopener noreferrer">
                        <img
                          src={r.proofImageUrl}
                          alt="proof"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 200,
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            cursor: "pointer",
                          }}
                        />
                      </a>
                      <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
                        Click image to open full size
                      </Text>
                    </div>
                  )}

                  {/* Admin note */}
                  <TextArea
                    placeholder="Admin note (required for rejection, optional for approval)"
                    value={notes[r._id] || ""}
                    onChange={(e) =>
                      setNotes((p) => ({ ...p, [r._id]: e.target.value }))
                    }
                    rows={2}
                    style={{ marginBottom: 14, borderRadius: 8 }}
                  />

                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      type="primary"
                      loading={loading[r._id]}
                      onClick={() => handleApprove(r._id)}
                      style={{
                        background: "#10b981", borderColor: "#10b981",
                        borderRadius: 8, fontWeight: 600,
                      }}
                    >
                      ✓ Approve (+2 days)
                    </Button>
                    <Button
                      danger
                      loading={loading[r._id]}
                      onClick={() => handleReject(r._id)}
                      style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                      ✗ Reject
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default ErrorReportApproval;